import { TypedRopNode } from "./_Base";
import { RopType } from "../../poly/registers/nodes/types/Rop";
import {
  LinearEncoding,
  sRGBEncoding,
  NoToneMapping,
  LinearToneMapping,
  ReinhardToneMapping,
  CineonToneMapping,
  ACESFilmicToneMapping,
  BasicShadowMap,
  PCFShadowMap,
  PCFSoftShadowMap,
  VSMShadowMap
} from "three";
import { NodeParamsConfig, ParamConfig } from "../utils/params/ParamsConfig";
import { CoreType } from "../../../core/Type";
import { Poly } from "../../Poly";
import { isBooleanTrue } from "../../../core/BooleanValue";
import { defaultPixelRatio } from "../../../core/camera/defaultPixelRatio";
var EncodingName = /* @__PURE__ */ ((EncodingName2) => {
  EncodingName2["Linear"] = "Linear";
  EncodingName2["sRGB"] = "sRGB";
  EncodingName2["RGBM7"] = "RGBM7";
  return EncodingName2;
})(EncodingName || {});
var EncodingValue = /* @__PURE__ */ ((EncodingValue2) => {
  EncodingValue2[EncodingValue2["Linear"] = LinearEncoding] = "Linear";
  EncodingValue2[EncodingValue2["sRGB"] = sRGBEncoding] = "sRGB";
  return EncodingValue2;
})(EncodingValue || {});
const ENCODING_NAMES = [
  "Linear" /* Linear */,
  "sRGB" /* sRGB */
];
const ENCODING_VALUES = [
  EncodingValue.Linear,
  EncodingValue.sRGB
];
export const DEFAULT_OUTPUT_ENCODING = EncodingValue.sRGB;
var ToneMappingName = /* @__PURE__ */ ((ToneMappingName2) => {
  ToneMappingName2["No"] = "No";
  ToneMappingName2["Linear"] = "Linear";
  ToneMappingName2["Reinhard"] = "Reinhard";
  ToneMappingName2["Cineon"] = "Cineon";
  ToneMappingName2["ACESFilmic"] = "ACESFilmic";
  return ToneMappingName2;
})(ToneMappingName || {});
var ToneMappingValue = /* @__PURE__ */ ((ToneMappingValue2) => {
  ToneMappingValue2[ToneMappingValue2["No"] = NoToneMapping] = "No";
  ToneMappingValue2[ToneMappingValue2["Linear"] = LinearToneMapping] = "Linear";
  ToneMappingValue2[ToneMappingValue2["Reinhard"] = ReinhardToneMapping] = "Reinhard";
  ToneMappingValue2[ToneMappingValue2["Cineon"] = CineonToneMapping] = "Cineon";
  ToneMappingValue2[ToneMappingValue2["ACESFilmic"] = ACESFilmicToneMapping] = "ACESFilmic";
  return ToneMappingValue2;
})(ToneMappingValue || {});
const TONE_MAPPING_NAMES = [
  "No" /* No */,
  "Linear" /* Linear */,
  "Reinhard" /* Reinhard */,
  "Cineon" /* Cineon */,
  "ACESFilmic" /* ACESFilmic */
];
const TONE_MAPPING_VALUES = [
  ToneMappingValue.No,
  ToneMappingValue.Linear,
  ToneMappingValue.Reinhard,
  ToneMappingValue.Cineon,
  ToneMappingValue.ACESFilmic
];
export const DEFAULT_TONE_MAPPING = ToneMappingValue.ACESFilmic;
const TONE_MAPPING_MENU_ENTRIES = TONE_MAPPING_NAMES.map((name, i) => {
  return {
    name,
    value: TONE_MAPPING_VALUES[i]
  };
});
var RendererPrecision = /* @__PURE__ */ ((RendererPrecision2) => {
  RendererPrecision2["HIGH"] = "highp";
  RendererPrecision2["MEDIUM"] = "mediump";
  RendererPrecision2["LOW"] = "lowp";
  return RendererPrecision2;
})(RendererPrecision || {});
const RENDERER_PRECISIONS = [
  "highp" /* HIGH */,
  "mediump" /* MEDIUM */,
  "lowp" /* LOW */
];
var PowerPreference = /* @__PURE__ */ ((PowerPreference2) => {
  PowerPreference2["HIGH"] = "high-performance";
  PowerPreference2["LOW"] = "low-power";
  PowerPreference2["DEFAULT"] = "default";
  return PowerPreference2;
})(PowerPreference || {});
const POWER_PREFERENCES = ["high-performance" /* HIGH */, "low-power" /* LOW */, "default" /* DEFAULT */];
var ShadowMapTypeName = /* @__PURE__ */ ((ShadowMapTypeName2) => {
  ShadowMapTypeName2["Basic"] = "Basic";
  ShadowMapTypeName2["PCF"] = "PCF";
  ShadowMapTypeName2["PCFSoft"] = "PCFSoft";
  ShadowMapTypeName2["VSM"] = "VSM";
  return ShadowMapTypeName2;
})(ShadowMapTypeName || {});
var ShadowMapTypeValue = /* @__PURE__ */ ((ShadowMapTypeValue2) => {
  ShadowMapTypeValue2[ShadowMapTypeValue2["Basic"] = BasicShadowMap] = "Basic";
  ShadowMapTypeValue2[ShadowMapTypeValue2["PCF"] = PCFShadowMap] = "PCF";
  ShadowMapTypeValue2[ShadowMapTypeValue2["PCFSoft"] = PCFSoftShadowMap] = "PCFSoft";
  ShadowMapTypeValue2[ShadowMapTypeValue2["VSM"] = VSMShadowMap] = "VSM";
  return ShadowMapTypeValue2;
})(ShadowMapTypeValue || {});
const SHADOW_MAP_TYPE_NAMES = [
  "Basic" /* Basic */,
  "PCF" /* PCF */,
  "PCFSoft" /* PCFSoft */,
  "VSM" /* VSM */
];
const SHADOW_MAP_TYPE_VALUES = [
  ShadowMapTypeValue.Basic,
  ShadowMapTypeValue.PCF,
  ShadowMapTypeValue.PCFSoft,
  ShadowMapTypeValue.VSM
];
export const SHADOW_MAP_TYPES = [BasicShadowMap, PCFShadowMap, PCFSoftShadowMap, VSMShadowMap];
export const DEFAULT_SHADOW_MAP_TYPE = ShadowMapTypeValue.PCFSoft;
const DEFAULT_PARAMS = {
  alpha: false,
  precision: "highp" /* HIGH */,
  premultipliedAlpha: true,
  antialias: false,
  stencil: true,
  preserveDrawingBuffer: false,
  powerPreference: "default" /* DEFAULT */,
  depth: true,
  logarithmicDepthBuffer: false
};
class WebGLRendererRopParamsConfig extends NodeParamsConfig {
  constructor() {
    super(...arguments);
    this.tprecision = ParamConfig.BOOLEAN(0);
    this.precision = ParamConfig.INTEGER(RENDERER_PRECISIONS.indexOf("highp" /* HIGH */), {
      visibleIf: { tprecision: 1 },
      menu: {
        entries: RENDERER_PRECISIONS.map((name, value) => {
          return { value, name };
        })
      }
    });
    this.tpowerPreference = ParamConfig.BOOLEAN(0);
    this.powerPreference = ParamConfig.INTEGER(POWER_PREFERENCES.indexOf("default" /* DEFAULT */), {
      visibleIf: { tpowerPreference: 1 },
      menu: {
        entries: POWER_PREFERENCES.map((name, value) => {
          return { value, name };
        })
      }
    });
    this.alpha = ParamConfig.BOOLEAN(1);
    this.premultipliedAlpha = ParamConfig.BOOLEAN(1);
    this.antialias = ParamConfig.BOOLEAN(1);
    this.stencil = ParamConfig.BOOLEAN(1);
    this.depth = ParamConfig.BOOLEAN(1);
    this.logarithmicDepthBuffer = ParamConfig.BOOLEAN(0);
    this.toneMapping = ParamConfig.INTEGER(DEFAULT_TONE_MAPPING, {
      menu: {
        entries: TONE_MAPPING_MENU_ENTRIES
      }
    });
    this.toneMappingExposure = ParamConfig.FLOAT(1, {
      range: [0, 2]
    });
    this.outputEncoding = ParamConfig.INTEGER(DEFAULT_OUTPUT_ENCODING, {
      menu: {
        entries: ENCODING_NAMES.map((name, i) => {
          return {
            name,
            value: ENCODING_VALUES[i]
          };
        })
      }
    });
    this.physicallyCorrectLights = ParamConfig.BOOLEAN(1);
    this.sortObjects = ParamConfig.BOOLEAN(1);
    this.tpixelRatio = ParamConfig.BOOLEAN(0);
    this.pixelRatio = ParamConfig.INTEGER(2, {
      visibleIf: { tpixelRatio: true },
      range: [1, 4],
      rangeLocked: [true, false]
    });
    this.tshadowMap = ParamConfig.BOOLEAN(1);
    this.shadowMapAutoUpdate = ParamConfig.BOOLEAN(1, { visibleIf: { tshadowMap: 1 } });
    this.shadowMapNeedsUpdate = ParamConfig.BOOLEAN(0, { visibleIf: { tshadowMap: 1 } });
    this.shadowMapType = ParamConfig.INTEGER(DEFAULT_SHADOW_MAP_TYPE, {
      visibleIf: { tshadowMap: 1 },
      menu: {
        entries: SHADOW_MAP_TYPE_NAMES.map((name, i) => {
          return {
            name,
            value: SHADOW_MAP_TYPE_VALUES[i]
          };
        })
      }
    });
  }
}
const ParamsConfig = new WebGLRendererRopParamsConfig();
export class WebGLRendererRopNode extends TypedRopNode {
  constructor() {
    super(...arguments);
    this.paramsConfig = ParamsConfig;
  }
  static type() {
    return RopType.WEBGL;
  }
  createRenderer(camera, canvas, gl) {
    const params = {};
    const keys = Object.keys(DEFAULT_PARAMS);
    let k;
    for (k of keys) {
      params[k] = DEFAULT_PARAMS[k];
    }
    if (isBooleanTrue(this.pv.tprecision)) {
      const precision = RENDERER_PRECISIONS[this.pv.precision];
      params.precision = precision;
    }
    if (isBooleanTrue(this.pv.tpowerPreference)) {
      const powerPreference = POWER_PREFERENCES[this.pv.powerPreference];
      params.powerPreference = powerPreference;
    }
    params.antialias = isBooleanTrue(this.pv.antialias);
    params.antialias = isBooleanTrue(this.pv.antialias);
    params.alpha = isBooleanTrue(this.pv.alpha);
    params.premultipliedAlpha = isBooleanTrue(this.pv.premultipliedAlpha);
    params.depth = isBooleanTrue(this.pv.depth);
    params.stencil = isBooleanTrue(this.pv.stencil);
    params.logarithmicDepthBuffer = isBooleanTrue(this.pv.logarithmicDepthBuffer);
    params.canvas = canvas;
    params.context = gl;
    const renderer = Poly.renderersController.createWebGLRenderer(params);
    if (Poly.renderersController.printDebug()) {
      Poly.renderersController.printDebugMessage(`create renderer from node '${this.path()}'`);
      Poly.renderersController.printDebugMessage({
        params
      });
    }
    this._updateRenderer(renderer);
    return renderer;
  }
  cook() {
    this._traverse_scene_and_update_materials();
    this.cookController.endCook();
  }
  _updateRenderer(renderer) {
    renderer.physicallyCorrectLights = this.pv.physicallyCorrectLights;
    renderer.outputEncoding = this.pv.outputEncoding;
    renderer.toneMapping = this.pv.toneMapping;
    renderer.toneMappingExposure = this.pv.toneMappingExposure;
    renderer.shadowMap.enabled = this.pv.tshadowMap;
    renderer.shadowMap.autoUpdate = this.pv.shadowMapAutoUpdate;
    renderer.shadowMap.needsUpdate = this.pv.shadowMapNeedsUpdate;
    renderer.shadowMap.type = this.pv.shadowMapType;
    renderer.sortObjects = this.pv.sortObjects;
    const pixelRatio = this.pv.tpixelRatio ? this.pv.pixelRatio : defaultPixelRatio();
    if (Poly.renderersController.printDebug()) {
      Poly.renderersController.printDebugMessage(`set renderer pixelRatio from '${this.path()}'`);
      Poly.renderersController.printDebugMessage({
        pixelRatio
      });
    }
    renderer.setPixelRatio(pixelRatio);
  }
  _traverse_scene_and_update_materials() {
    this.scene().threejsScene().traverse((object) => {
      const material = object.material;
      if (material) {
        if (CoreType.isArray(material)) {
          for (let mat of material) {
            mat.needsUpdate = true;
          }
        } else {
          material.needsUpdate = true;
        }
      }
    });
  }
}
